import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  InputAdornment,
  Alert,
  Snackbar,
  Grid,
  Divider,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  RefreshCw,
  RotateCcw,
} from 'lucide-react';
import {
  createReturn,
} from '../firebase/returnsService';
import { getBills, updateBill } from '../firebase/billingService';
import { getInventoryItems, updateStock } from '../firebase/inventoryService';
import { restoreStockFIFO } from '../firebase/stockBatchService';
import { updateCustomerStats } from '../firebase/customerService';
import { recordBillReturn } from '../firebase/creditorService';
import { debitCashAccount } from '../firebase/cashBankService';
import { logActivity, ACTIONS, ENTITY_TYPES } from '../firebase/activityLogService';
import useFirestoreStore from '../stores/useFirestoreStore';

// Helper: local date string YYYY-MM-DD (avoids UTC timezone shift)
const localDateStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const Returns = ({ currentUser }) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Read returns from global store — no extra listener
  const returns = useFirestoreStore((state) => state.returns);

  const [bills, setBills] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [returnItems, setReturnItems] = useState([]);
  const [returnReason, setReturnReason] = useState('');
  const [refundAmount, setRefundAmount] = useState(0);
  const [refundDeliveryCharge, setRefundDeliveryCharge] = useState(false);
  const [restockItems, setRestockItems] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [submitting, setSubmitting] = useState(false);

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    const [billsResult, inventoryResult] = await Promise.all([
      getBills(),
      getInventoryItems()
    ]);
    
    if (billsResult.success) setBills(billsResult.data);
    if (inventoryResult.success) setInventory(inventoryResult.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSelectBill = (bill) => {
    setSelectedBill(bill);

    // Calculate already-returned quantities per item from existing returns
    const existingReturns = returns.filter(r => r.billId === bill.id);
    const alreadyReturnedQty = {}; // itemId -> total qty already returned
    existingReturns.forEach(ret => {
      (ret.items || []).forEach(item => {
        alreadyReturnedQty[item.id] = (alreadyReturnedQty[item.id] || 0) + (item.quantity || 0);
      });
    });

    // Only show items that still have returnable quantity
    const items = bill.items
      .map(item => {
        const alreadyReturned = alreadyReturnedQty[item.id] || 0;
        const remainingQty = item.quantity - alreadyReturned;
        return {
          ...item,
          quantity: remainingQty,         // max returnable
          returnQuantity: remainingQty,   // default to full remaining
          selected: remainingQty > 0,
        };
      })
      .filter(item => item.quantity > 0); // hide fully returned items

    setReturnItems(items);

    // Calculate initial refund based on remaining items
    const subtotal = items.reduce((s, i) => s + i.price * i.returnQuantity, 0);
    const originalSubtotal = bill.subtotal || bill.total || 1;
    const returnRatio = Math.min(subtotal / originalSubtotal, 1);
    let refund = subtotal;
    if (bill.discount?.value > 0) refund -= (bill.discount.amount || 0) * returnRatio;
    if (bill.loyaltyDiscount?.applied) refund -= (bill.loyaltyDiscount.amount || 0) * returnRatio;
    setRefundAmount(Math.max(0, refund));

    setShowModal(true);
  };

  const handleItemSelection = (index, checked) => {
    const updatedItems = [...returnItems];
    updatedItems[index].selected = checked;
    setReturnItems(updatedItems);
    calculateRefund(updatedItems);
  };

  const handleQuantityChange = (index, quantity) => {
    const updatedItems = [...returnItems];
    const maxQty = updatedItems[index].quantity;
    const newQty = Math.min(Math.max(0, parseInt(quantity) || 0), maxQty);
    updatedItems[index].returnQuantity = newQty;
    setReturnItems(updatedItems);
    calculateRefund(updatedItems);
  };

  const calculateRefund = (items) => {
    const selectedItems = items.filter(item => item.selected);
    const subtotal = selectedItems.reduce((sum, item) => {
      return sum + (item.price * item.returnQuantity);
    }, 0);

    const originalSubtotal = selectedBill?.subtotal || selectedBill?.total || 1;
    const returnRatio = subtotal / originalSubtotal;

    let refund = subtotal;

    // Proportional regular discount
    if (selectedBill?.discount?.value > 0) {
      refund -= (selectedBill.discount.amount || 0) * returnRatio;
    }

    // Proportional loyalty discount
    if (selectedBill?.loyaltyDiscount?.applied && selectedBill?.loyaltyDiscount?.amount > 0) {
      refund -= selectedBill.loyaltyDiscount.amount * returnRatio;
    }

    setRefundAmount(Math.max(0, refund));
  };

  const handleProcessReturn = async () => {
    if (submitting) return;
    
    if (!returnReason.trim()) {
      showSnackbar('Please provide a reason for the return', 'error');
      return;
    }

    const selectedItems = returnItems.filter(item => item.selected && item.returnQuantity > 0);
    if (selectedItems.length === 0) {
      showSnackbar('Please select at least one item to return', 'error');
      return;
    }

    // Calculate total refund including optional delivery charge
    const deliveryRefund = refundDeliveryCharge ? (selectedBill?.deliveryCharge || 0) : 0;
    const totalRefund = refundAmount + deliveryRefund;

    setSubmitting(true);

    try {
      // 1. Create return record
      const returnData = {
        billId: selectedBill.id,
        invoiceNumber: selectedBill.invoiceNumber || selectedBill.id,
        customer: selectedBill.customer,
        customerType: selectedBill.customerType,
        paymentType: selectedBill.paymentType,
        originalTotal: selectedBill.total,
        refundAmount: totalRefund,
        deliveryChargeRefunded: deliveryRefund,
        items: selectedItems.map(item => ({
          id: item.id,
          name: item.name,
          category: item.category,
          itemCode: item.itemCode || '',
          quantity: item.returnQuantity,
          price: item.price,
          subtotal: item.price * item.returnQuantity
        })),
        reason: returnReason,
        restockItems: restockItems,
        status: 'Completed',
        processedBy: currentUser?.email || 'Unknown',
        date: localDateStr()
      };

      const returnResult = await createReturn(returnData);
      
      if (!returnResult.success) {
        throw new Error(returnResult.error || 'Failed to create return record');
      }

      // 2. Restore inventory if restocking
      if (restockItems) {
        // Group decant items by inventoryId to handle multiple sizes from same source
        const decantRestoreMap = {}; // inventoryId -> total ML to restore
        const regularItems = [];

        for (const item of selectedItems) {
          const inventoryId = item.inventoryId || item.id;
          const inventoryItem = inventory.find(inv => inv.id === inventoryId);

          if (inventoryItem?.trackInML) {
            const mlToRestore = item.returnQuantity * (item.decantML || 1);
            decantRestoreMap[inventoryId] = (decantRestoreMap[inventoryId] || 0) + mlToRestore;
          } else {
            regularItems.push(item);
          }
        }

        // Restore decant ML — one write per inventory item
        for (const [inventoryId, mlToRestore] of Object.entries(decantRestoreMap)) {
          const inventoryItem = inventory.find(inv => inv.id === inventoryId);
          if (inventoryItem) {
            await updateStock(inventoryId, (inventoryItem.stock || 0) + mlToRestore);
          }
        }

        // Restore regular items via FIFO
        const stockUpdatePromises = regularItems.map(async (item) => {
          const billItemBatchData = selectedBill.batchesUsed?.find(b => b.itemId === item.id);

          if (billItemBatchData?.batchesUsed?.length > 0) {
            // FIFO batch restoration
            const originalQty = billItemBatchData.quantity;
            const returnQty = item.returnQuantity;

            let batchesToRestore;

            if (returnQty >= originalQty) {
              // Full return — restore all batches as-is
              batchesToRestore = billItemBatchData.batchesUsed;
            } else {
              // Partial return — proportionally scale down batches (oldest first)
              batchesToRestore = [];
              let remaining = returnQty;

              for (const batch of billItemBatchData.batchesUsed) {
                if (remaining <= 0) break;
                const restoreQty = Math.min(batch.quantityUsed, remaining);
                batchesToRestore.push({ ...batch, quantityUsed: restoreQty });
                remaining -= restoreQty;
              }
            }

            return restoreStockFIFO(item.id, batchesToRestore);
          } else {
            // Old bill with no batchesUsed — fall back to flat stock update
            const inventoryItem = inventory.find(inv => inv.id === item.id);
            if (inventoryItem) {
              const newStock = inventoryItem.stock + item.returnQuantity;
              return updateStock(item.id, newStock);
            }
          }
          return { success: true };
        });

        await Promise.all(stockUpdatePromises);

        // Update local inventory state
        setInventory(prevInventory => prevInventory.map(inv => {
          // For decant items, sum all ML restored for this inventory item
          const decantML = decantRestoreMap[inv.id];
          if (decantML) {
            return { ...inv, stock: inv.stock + decantML };
          }
          // For regular items
          const returnedItem = regularItems.find(item => item.id === inv.id);
          if (returnedItem) {
            return { ...inv, stock: inv.stock + returnedItem.returnQuantity };
          }
          return inv;
        }));
      }

      // 3. Calculate if this is a full return (needed for customer stats, creditor and bill status)
      const existingReturns = returns.filter(r => r.billId === selectedBill.id);
      const totalReturnedQty = {};
      existingReturns.forEach(ret => {
        (ret.items || []).forEach(item => {
          totalReturnedQty[item.id] = (totalReturnedQty[item.id] || 0) + (item.quantity || 0);
        });
      });
      selectedItems.forEach(item => {
        totalReturnedQty[item.id] = (totalReturnedQty[item.id] || 0) + item.returnQuantity;
      });
      const isFullReturn = selectedBill.items.every(
        item => (totalReturnedQty[item.id] || 0) >= item.quantity
      );

      // 3.5. Update customer stats (reduce totalSpent; decrement totalOrders only on full return)
      const customers = await import('../firebase/customerService').then(m => m.getCustomers());
      if (customers.success) {
        const customer = customers.data.find(c => c.name === selectedBill.customer);
        if (customer) {
          const newTotalSpent = Math.max(0, (customer.totalSpent || 0) - totalRefund);
          const newTotalOrders = isFullReturn
            ? Math.max(0, (customer.totalOrders || 0) - 1)
            : customer.totalOrders;
          await updateCustomerStats(customer.id, newTotalOrders, newTotalSpent);
        }
      }

      // 4. Handle creditor adjustments for Credit/COD payments
      if (selectedBill.paymentType === 'Credit' || selectedBill.paymentType === 'COD' || selectedBill.paymentType === 'Koko' || selectedBill.paymentType === 'MintPay') {
        if (selectedBill.creditorId) {
          // Record return (reduces totalCredited, NOT a payment)
          const returnType = isFullReturn ? 'full' : 'partial';
          await recordBillReturn(
            selectedBill.creditorId,
            selectedBill.id,
            totalRefund,
            returnType
          );
        }
      }

      // 5. Debit cash/bank account for Cash or Card refunds
      if (selectedBill.paymentType === 'Cash' || selectedBill.paymentType === 'Card') {
        const accountType = selectedBill.paymentType === 'Card' ? 'cash_at_bank' : 'petty_cash';
        debitCashAccount(
          accountType,
          totalRefund,
          selectedBill.id,
          'return',
          `Return/Refund - ${selectedBill.invoiceNumber || selectedBill.id} - ${selectedBill.customer}`,
          selectedBill.paymentType.toLowerCase(),
          null,
          currentUser
        ).catch(err => console.error('Cash ledger debit failed on return:', err));
      }

      // 6. Update bill status to indicate partial/full return
      // (isFullReturn already calculated in step 3.5)
      await updateBill(selectedBill.id, {
        ...selectedBill,
        returnStatus: isFullReturn ? 'Fully Returned' : 'Partially Returned',
        status: isFullReturn ? 'Returned' : selectedBill.status,
        returnAmount: (selectedBill.returnAmount || 0) + totalRefund,
        lastReturnDate: localDateStr()
      });

      // 8. Log activity
      await logActivity({
        userId: currentUser?.uid,
        userEmail: currentUser?.email,
        userName: currentUser?.displayName || currentUser?.email,
        action: ACTIONS.CREATED_RETURN,
        entityType: ENTITY_TYPES.RETURN,
        entityId: returnResult.id,
        entityName: `Return for Invoice ${selectedBill.invoiceNumber || selectedBill.id}`,
        details: {
          billId: selectedBill.id,
          customer: selectedBill.customer,
          refundAmount: totalRefund,
          deliveryChargeRefunded: deliveryRefund,
          itemsReturned: selectedItems.length,
          restocked: restockItems,
          reason: returnReason
        }
      });

      showSnackbar('Return processed successfully. Inventory and sales have been updated.');
      closeModal();
      await loadData();

    } catch (error) {
      console.error('Error processing return:', error);
      showSnackbar(error.message || 'Failed to process return', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedBill(null);
    setReturnItems([]);
    setReturnReason('');
    setRefundAmount(0);
    setRefundDeliveryCharge(false);
    setRestockItems(true);
  };

  const filteredBills = bills.filter(bill => {
    const searchLower = searchTerm.toLowerCase();
    return (
      bill.customer?.toLowerCase().includes(searchLower) ||
      bill.id?.toLowerCase().includes(searchLower) ||
      bill.invoiceNumber?.toLowerCase().includes(searchLower)
    );
  });

  const filteredReturns = returns.filter(ret => {
    const searchLower = searchTerm.toLowerCase();
    return (
      ret.customer?.toLowerCase().includes(searchLower) ||
      ret.invoiceNumber?.toLowerCase().includes(searchLower) ||
      ret.billId?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Returns & Refunds
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Process returns and manage refunds with automatic inventory and sales reversals
          </Typography>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Returns
              </Typography>
              <Typography variant="h4" fontWeight={700}>
                {returns.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Refunded
              </Typography>
              <Typography variant="h4" fontWeight={700} color="error.main">
                LKR {returns.reduce((sum, ret) => sum + (ret.refundAmount || 0), 0).toLocaleString('en-LK', { minimumFractionDigits: 2 })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Items Returned
              </Typography>
              <Typography variant="h4" fontWeight={700}>
                {returns.reduce((sum, ret) => sum + (ret.items?.length || 0), 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                This Month
              </Typography>
              <Typography variant="h4" fontWeight={700}>
                {returns.filter(ret => {
                  const d = new Date();
                  const monthStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                  return ret.date?.startsWith(monthStr);
                }).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Bills Available for Return */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              Select Invoice for Return
            </Typography>
            <Button
              variant="outlined"
              startIcon={refreshing ? <CircularProgress size={20} /> : <RefreshCw size={18} />}
              onClick={handleRefresh}
              disabled={refreshing}
              size="small"
              color="primary"
            >
              Refresh
            </Button>
          </Box>

          <TextField
            fullWidth
            placeholder="Search by customer or invoice number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon size={20} />
                </InputAdornment>
              ),
            }}
          />

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Payment Type</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredBills.slice(0, 10).map((bill) => (
                  <TableRow key={bill.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} color="primary">
                        {bill.invoiceNumber || bill.id}
                      </Typography>
                    </TableCell>
                    <TableCell>{bill.customer}</TableCell>
                    <TableCell>{bill.date}</TableCell>
                    <TableCell>
                      <Chip label={bill.paymentType || 'Cash'} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {(bill.total || 0).toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {bill.returnStatus ? (
                        <Chip 
                          label={bill.returnStatus} 
                          size="small" 
                          color={bill.returnStatus === 'Fully Returned' ? 'error' : 'warning'}
                          sx={{
                            fontWeight: 700,
                            '& .MuiChip-label': {
                              px: 1,
                            }
                          }}
                        />
                      ) : (
                        <Chip 
                          label="No Returns" 
                          size="small" 
                          color="success"
                          sx={{
                            fontWeight: 700,
                            '& .MuiChip-label': {
                              px: 1,
                            }
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => handleSelectBill(bill)}
                        disabled={bill.returnStatus === 'Fully Returned'}
                      >
                        <RotateCcw size={18} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredBills.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No invoices found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Returns History */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
            Returns History
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Items</TableCell>
                  <TableCell align="right">Refund Amount</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Processed By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredReturns.map((ret) => (
                  <TableRow key={ret.id} hover>
                    <TableCell>{ret.date}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} color="primary">
                        {ret.invoiceNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>{ret.customer}</TableCell>
                    <TableCell>{ret.items?.length || 0} item(s)</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600} color="error.main">
                        {(ret.refundAmount || 0).toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" noWrap sx={{ maxWidth: 200, display: 'block' }}>
                        {ret.reason}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{ret.processedBy}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredReturns.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No returns found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Process Return Dialog */}
      <Dialog 
        open={showModal} 
        onClose={closeModal} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: 'calc(100vh - 64px)',
          }
        }}
      >
        <DialogTitle>
          Process Return - Invoice #{selectedBill?.invoiceNumber || selectedBill?.id}
        </DialogTitle>
        <DialogContent sx={{ overflow: 'auto' }}>
          <Box sx={{ pt: 2 }}>


            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Customer:</Typography>
                <Typography variant="body1" fontWeight={600}>{selectedBill?.customer}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Payment Type:</Typography>
                <Typography variant="body1" fontWeight={600}>{selectedBill?.paymentType}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Original Amount:</Typography>
                <Typography variant="body1" fontWeight={600}>
                  LKR {(selectedBill?.total || 0).toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Date:</Typography>
                <Typography variant="body1" fontWeight={600}>{selectedBill?.date}</Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Select Items to Return
            </Typography>

            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">Select</TableCell>
                    <TableCell>Item</TableCell>
                    <TableCell align="center">Sold Qty</TableCell>
                    <TableCell align="center">Return Qty</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell align="right">Refund</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {returnItems.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={item.selected}
                          onChange={(e) => handleItemSelection(index, e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {item.name} ({item.category})
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{item.quantity}</TableCell>
                      <TableCell align="center">
                        <TextField
                          type="number"
                          size="small"
                          value={item.returnQuantity}
                          onChange={(e) => handleQuantityChange(index, e.target.value)}
                          disabled={!item.selected}
                          sx={{ width: 70 }}
                          inputProps={{ min: 0, max: item.quantity }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        LKR {item.price.toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          LKR {(item.price * item.returnQuantity).toFixed(2)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TextField
              fullWidth
              label="Reason for Return"
              multiline
              rows={3}
              value={returnReason}
              onChange={(e) => setReturnReason(e.target.value)}
              required
              sx={{ mb: 2 }}
              placeholder="e.g., Defective product, Wrong item, Customer changed mind..."
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={restockItems}
                  onChange={(e) => setRestockItems(e.target.checked)}
                />
              }
              label="Restock items to inventory"
              sx={{ mb: 2 }}
            />

            <Divider sx={{ my: 2 }} />

            <Box sx={{ backgroundColor: 'action.hover', p: 2, borderRadius: 2 }}>
              {/* Refund delivery charge option for COD bills */}
              {selectedBill?.paymentType === 'COD' && selectedBill?.deliveryCharge > 0 && (
                <Box sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={refundDeliveryCharge}
                        onChange={(e) => setRefundDeliveryCharge(e.target.checked)}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        Refund delivery charge (LKR {(selectedBill.deliveryCharge).toLocaleString('en-LK', { minimumFractionDigits: 2 })})
                      </Typography>
                    }
                  />
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6" fontWeight={700}>Total Refund Amount:</Typography>
                <Typography variant="h6" fontWeight={700} color="error.main">
                  LKR {(refundAmount + (refundDeliveryCharge ? (selectedBill?.deliveryCharge || 0) : 0)).toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                </Typography>
              </Box>
              {refundDeliveryCharge && selectedBill?.deliveryCharge > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Products: LKR {refundAmount.toLocaleString('en-LK', { minimumFractionDigits: 2 })} + Delivery: LKR {(selectedBill.deliveryCharge).toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeModal} disabled={submitting}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleProcessReturn}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <RotateCcw size={18} />}
            sx={{
              backgroundColor: 'error.main',
              color: 'common.white',
              '&:hover': {
                backgroundColor: 'error.dark'
              }
            }}
          >
            {submitting ? 'Processing...' : 'Process Return'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Returns;
